import React, { useEffect, useState, useRef } from 'react';
import _isEmpty from 'lodash.isempty';
import { FormModal, Loader, useSocketContext } from '@shootsta/common-react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import FlexPurchasePaymentSuccessModal from '../../FlexPurchasePaymentSuccessModal';
import PaymentFailedModal from '../../PaymentFailedModal';
import PaymentSuccessfulModal from '../../PaymentSuccessfulModal';
import { PAYMENT_TYPES } from '../../../constants';
import {
  parseTopupPromo,
  promoBonusFor
} from '../../TopupCreditsModal/utils/topupPromo';

interface PaymentStatusModalProps {
  history: any;
  getPaymentInvoice: Function;
  getTransactionByOrderId: Function;
  getContracts: Function;
  onCreditsUpdated?: () => void;
}

interface LineItem {
  price_data: {
    unit_amount: number | string;
  };
  quantity: number;
}

interface PaymentSuccessEventType {
  orderId: string;
  status: string;
  totalCreditPurchased: number;
  promoBonusCredits?: number;
}

enum PaymentStatusEnum {
  PROCESSING = 'processing',
  SUCCESS = 'success',
  CANCELLED = 'cancelled'
}

function parsePaymentSearchParams(location: any) {
  const defaultParams = {
    status: null,
    orderId: null,
    type: null
  };

  let searchString = location.search;

  const hashWithParams = location.hash || '';
  const hashQueryIndex = hashWithParams.indexOf('?');
  if (hashQueryIndex !== -1) {
    searchString = hashWithParams.substring(hashQueryIndex);
  }

  if (_isEmpty(searchString)) {
    return defaultParams;
  }

  const searchParams = new URLSearchParams(searchString);

  const status = searchParams.get('paymentStatus');
  const orderId = searchParams.get('orderId');
  const type = searchParams.get('type');

  return {
    status,
    orderId,
    type
  };
}

function PaymentStatusModal({
  history,
  getPaymentInvoice,
  getTransactionByOrderId,
  getContracts,
  onCreditsUpdated
}: PaymentStatusModalProps) {
  const topUpValue = useRef(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollIntervalRef = useRef<NodeJS.Timer | null>(null);

  const { socket } = useSocketContext();

  const {
    location: { search, hash },
    location
  } = history;

  const {
    status,
    orderId: orderIdParam,
    type
  } = parsePaymentSearchParams(location);

  const [paymentStatus, setPaymentStatus] = useState<PaymentStatusEnum | null>(
    status as PaymentStatusEnum
  );
  const [orderId, setOrderId] = useState<string | null>(orderIdParam);
  const [orderType, setOrderType] = useState<string | null>(type);
  const [purchasedCredit, setPurchasedCredit] = useState(0);
  const [promoBonusCredits, setPromoBonusCredits] = useState(0);
  const flags = useFlags() || {};

  const clearAllTimers = () => {
    [timeoutRef, pollIntervalRef].forEach((ref) => {
      if (ref.current) {
        clearTimeout(ref.current);
        ref.current = null;
      }
    });
  };

  function onClosePaymentModal() {
    clearAllTimers();
    setPaymentStatus(null);
  }

  const pollOrderStatus = async () => {
    const transactionResponse = await getTransactionByOrderId({ orderId });
    const { status, lineItems } =
      transactionResponse?.data?.getTransactionByOrderId || {};

    if (!status || status === 'OPEN') return;

    const isSuccess = status === 'SUCCEEDED';

    if (isSuccess) {
      setPaymentStatus(PaymentStatusEnum.SUCCESS);
      // Refresh the platform balance so credits/dollars update without a manual
      // page refresh — the ledger is already written by the time we see SUCCEEDED.
      onCreditsUpdated?.();
    }

    if (isSuccess && orderType === PAYMENT_TYPES.FLEX) {
      const latestTopUpVal = topUpValue.current;

      const credits = lineItems.reduce(
        (total: number, item: LineItem): number =>
          total +
          (Number(item.price_data.unit_amount) * Number(item.quantity)) /
            (latestTopUpVal * 100),
        0
      );

      setPurchasedCredit(credits);
      // The order poll carries no bonus, so this fallback works it out from
      // the live promo; the socket event below carries the granted amount.
      setPromoBonusCredits(
        promoBonusFor(credits, parseTopupPromo(flags.topupCreditsPromos))
      );
    }

    clearAllTimers();
  };

  function handleOrderEvent(order: PaymentSuccessEventType) {
    const {
      orderId: receivedOrderId,
      totalCreditPurchased,
      promoBonusCredits: grantedBonus
    } = order;

    if (orderId !== receivedOrderId) {
      return;
    }

    setPaymentStatus(PaymentStatusEnum.SUCCESS);
    setPurchasedCredit(totalCreditPurchased);
    setPromoBonusCredits(Number(grantedBonus) || 0);
    // Refresh the platform balance so credits/dollars update without a manual
    // page refresh — the ledger is already written by the time this event fires.
    onCreditsUpdated?.();

    if (!timeoutRef.current) {
      return;
    }

    clearAllTimers();
  }

  async function onClickViewInvoice() {
    const { data } = await getPaymentInvoice({
      orderId,
      isUrl: true
    });
    const { invoiceUrl } = data?.getPaymentInvoice?.[0] || {};

    if (invoiceUrl) window.open(invoiceUrl, '_blank');

    onClosePaymentModal();
  }

  function removeUrlParams(param: string) {
    const { hash, search, pathname } = location;

    const hasParamsInHash = hash && hash.includes('?');
    const hashBase = hasParamsInHash ? hash.split('?')[0] : hash;
    const searchString = hasParamsInHash ? `?${hash.split('?')[1]}` : search;

    if (_isEmpty(searchString)) return;

    const queryParams = new URLSearchParams(searchString);
    queryParams.delete(param);

    const newSearch = queryParams.toString();
    history.replace({
      pathname,
      search: !hasParamsInHash ? newSearch : '',
      hash: hasParamsInHash
        ? `${hashBase}${newSearch ? `?${newSearch}` : ''}`
        : hash
    });
  }

  useEffect(() => {
    const fetchContractData = async () => {
      const contractsResponse = await getContracts({
        status: 'ACTIVE',
        product: 'WORKSPACE'
      });
      const activeContract = contractsResponse?.data?.getContracts?.[0];

      topUpValue.current =
        activeContract?.Payload?.subscription?.TOP_UP_CREDIT_VALUE ?? 1;
    };

    if (type === PAYMENT_TYPES.FLEX) fetchContractData();
  }, [type, getContracts]);

  useEffect(() => {
    socket.connect();

    const onConnect = () => console.log('Socket connected');
    const onDisconnect = () => console.log('Socket disconnected');

    socket.on('connect', onConnect);
    socket.on('connect_error', onDisconnect);
    socket.on('error', onDisconnect);
    socket.on('disconnect', onDisconnect);

    socket.on(`orderId|${orderId}`, (order: PaymentSuccessEventType) => {
      handleOrderEvent(order);
    });

    return () => {
      socket.disconnect();

      if (!timeoutRef.current) {
        return;
      }
      clearAllTimers();
    };
  }, []);

  useEffect(() => {
    if (_isEmpty(search) && !hash?.includes('?')) return;

    const { status, orderId, type } = parsePaymentSearchParams(location);

    if (
      type === PAYMENT_TYPES.PROJECT_SUBMISSION &&
      status === PaymentStatusEnum.SUCCESS
    )
      return;

    if (type) {
      setOrderType(type);
      removeUrlParams('type');
    }

    if (orderId) {
      setOrderId(orderId);
      removeUrlParams('orderId');
    }

    if (!status) {
      return;
    }

    if (status === PaymentStatusEnum.SUCCESS) {
      setPaymentStatus(PaymentStatusEnum.PROCESSING);
      removeUrlParams('paymentStatus');

      timeoutRef.current = setTimeout(
        () => window.location.reload(),
        1 * 60 * 1000
      );

      // start polling after 10s only if socket fails, gets cleared if socket event is received
      pollIntervalRef.current = setInterval(pollOrderStatus, 5000);
      return;
    }

    setPaymentStatus(status as PaymentStatusEnum);
    removeUrlParams('paymentStatus');
  }, [search, hash]);

  if (paymentStatus === PaymentStatusEnum.PROCESSING) {
    return (
      <FormModal visible hideFooter showHeaderCloseButton={false}>
        <Loader
          hasNoBeforeShowTimeout
          text="Please wait a moment while we securely process your payment. Do not close or refresh the page."
          caption={null}
        />
      </FormModal>
    );
  }

  if (paymentStatus === PaymentStatusEnum.SUCCESS) {
    if (orderType === PAYMENT_TYPES.FLEX) {
      return (
        <FlexPurchasePaymentSuccessModal
          visible
          purchasedCredit={purchasedCredit}
          promoBonusCredits={promoBonusCredits}
          onViewInvoice={onClickViewInvoice}
          onClose={onClosePaymentModal}
        />
      );
    }

    if (orderType === PAYMENT_TYPES.PROJECT_COMPLETED) {
      return (
        <PaymentSuccessfulModal
          visible
          onViewInvoice={onClickViewInvoice}
          onClose={onClosePaymentModal}
        />
      );
    }

    if (orderType === PAYMENT_TYPES.PROJECT_SUBMISSION) return;
  }

  if (paymentStatus === PaymentStatusEnum.CANCELLED) {
    return <PaymentFailedModal visible onClose={onClosePaymentModal} />;
  }

  return null;
}

export default PaymentStatusModal;
